/**
 * TopBar — sticky, blurred app chrome: bolt logo + workspace pill, primary nav,
 * and sign-out. Nav is hidden during onboarding (pass `showNav={false}`), but the
 * bar itself stays for consistent chrome. Presentational — the caller owns session
 * state, the role, and the sign-out handler.
 *
 * Sources is owner-only (OWNER_ONLY_PATHS): a plain member sees Dashboard and
 * Settings. AuthedLayout redirects members who reach an owner-only URL directly,
 * so hiding the link here is the cosmetic half of one rule.
 *
 * Settings deliberately stays in a member's nav — it holds the only mint/revoke
 * UI for their own MCP key. The page itself hides its owner-only sections
 * (settings.tsx).
 */
import { Link, useRouterState } from '@tanstack/react-router';
import { Layers, LayoutDashboard, LogOut, Send, SlidersHorizontal } from 'lucide-react';
import { WorkspaceSwitcher } from '@/components/layout/workspace-switcher';
import { BrandMark } from '@/components/patterns/brand-mark';

const NAV_ITEMS = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, ownerOnly: false },
  { label: 'Sources', to: '/sources', icon: Layers, ownerOnly: true },
  { label: 'Destinations', to: '/destinations', icon: Send, ownerOnly: true },
  { label: 'Settings', to: '/settings', icon: SlidersHorizontal, ownerOnly: false },
] as const;

/**
 * Route prefixes only an owner may open. Every /sources child route (add,
 * import, collection + account detail) is covered by the prefix match — same
 * for /destinations, which configures what leaves the workspace.
 */
export const OWNER_ONLY_PATHS = ['/sources', '/destinations'] as const;

export function isOwnerOnlyPath(pathname: string): boolean {
  return OWNER_ONLY_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export interface TopBarProps {
  showNav: boolean;
  /** Whether the signed-in user owns the active workspace; gates the nav items. */
  isOwner: boolean;
  onSignOut: () => void;
}

export function TopBar({ showNav, isOwner, onSignOut }: TopBarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navItems = NAV_ITEMS.filter((item) => isOwner || !item.ownerOnly);

  return (
    <header className="sticky top-0 z-10 flex h-[60px] items-center justify-between border-b border-border-subtle bg-background/85 px-7 backdrop-blur">
      {/* Brand */}
      <div className="flex items-center">
        <span className="flex text-foreground">
          <BrandMark size={18} />
        </span>
        <span className="ml-2 text-base font-bold tracking-[-0.01em]">Brain</span>
        <WorkspaceSwitcher />
      </div>

      {/* Primary nav (hidden during onboarding) */}
      {showNav ? (
        <nav className="flex items-center gap-1">
          {navItems.map(({ label, to, icon: Icon }) => {
            const active = pathname === to || pathname.startsWith(`${to}/`);
            return (
              <Link
                key={to}
                to={to}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  active
                    ? 'bg-accent text-accent-foreground font-semibold'
                    : 'text-faint hover:text-foreground font-medium'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </nav>
      ) : null}

      {/* Sign out */}
      <button
        type="button"
        onClick={onSignOut}
        className="flex items-center gap-2 text-sm text-faint transition-colors hover:text-foreground"
      >
        <LogOut className="h-4 w-4" />
        Sign out
      </button>
    </header>
  );
}
